import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Notification {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

interface Dialog {
  open: boolean;
  type: 'add-item' | 'edit-item' | 'confirm-delete' | 'checkout' | 'checkin' | 'qr-scanner' | 'qr-code' | 'settings' | 'item-detail' | 'import';
  data?: any;
}

interface UIState {
  sidebarOpen: boolean;
  notifications: Notification[];
  dialog: Dialog;
  loading: {
    global: boolean;
    checkout: boolean;
    checkin: boolean;
    delete: boolean;
  };
  theme: 'light' | 'dark';
}

const initialState: UIState = {
  sidebarOpen: false,
  notifications: [],
  dialog: {
    open: false,
    type: 'add-item',
  },
  loading: {
    global: false,
    checkout: false,
    checkin: false,
    delete: false,
  },
  theme: 'light',
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
      const notification: Notification = {
        id: Date.now().toString(),
        ...action.payload,
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    openDialog: (state, action: PayloadAction<{ type: Dialog['type']; data?: any }>) => {
      state.dialog = {
        open: true,
        type: action.payload.type,
        data: action.payload.data,
      };
    },
    closeDialog: (state) => {
      state.dialog = {
        open: false,
        type: 'add-item',
        data: null,
      };
    },
    setLoading: (state, action: PayloadAction<{ key: keyof UIState['loading']; value: boolean }>) => {
      state.loading[action.payload.key] = action.payload.value;
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload;
    },
  },
});

export const {
  toggleSidebar,
  setSidebarOpen,
  addNotification,
  removeNotification,
  clearNotifications,
  openDialog,
  closeDialog,
  setLoading,
  setTheme,
} = uiSlice.actions;

export default uiSlice.reducer;